import React, { useMemo } from 'react';

import { Box } from '@tanium/react-box';
import styled from '@tanium/react-emotion-9';
import { useLocale } from '@tanium/react-locale-context';
import { Stack, StackChild } from '@tanium/react-stack';
import { css } from '@tanium/style-system';
import { ThemeFontWeights } from '@tanium/theme-data';

import { useLocalizedLabelPercentFormatter } from '../../hooks/useLocalizedLabelPercentFormatter';
import { useLocalizedPercentFormatter } from '../../hooks/useLocalizedPercentFormatter';
import { useLocalizedStandardNumberFormatter } from '../../hooks/useLocalizedStandardNumberFormatter';
import { useTranslation } from '../../localization';
import { TooltipRenderFnProps } from '../../utils/ChartProps';
import { DataKeyGetter, DataKeyType, LabelGetter, ValueGetter } from '../../utils/DataProps';
import { LegendItem } from '../Legend';
import { NoWrapText } from '../utils/NoWrapText';
import { getLocalFormattedDate } from './getLocalFormattedDate';
import { getUTCFormattedDate } from './getUTCFormattedDate';
import { sliceCenter } from './sliceCenter';
import { ActiveFill, DateLabelText, DateValueText, LabelText, SubtleText } from './themeDefinitions';

const TOOLTIP_CONTAINER_PADDING = 16;
const TOOLTIP_CONTENT_WIDTH = 300 - TOOLTIP_CONTAINER_PADDING;
const TOOLTIP_MAX_ITEMS = 16;

export interface DefaultTooltipContentProps<TValueType extends number, TDataKeyType extends DataKeyType, TDatum>
  extends Pick<TooltipRenderFnProps<TDataKeyType, TDatum>, 'data' | 'colorScale' | 'date'> {
  dataKeyGetter: DataKeyGetter<TDatum, TDataKeyType>;
  labelGetter: LabelGetter<TDatum>;
  /**
   * A title to display at the top of the tooltip.
   */
  title?: React.ReactNode;
  valueGetter: ValueGetter<TDatum, TValueType>;
  /**
   * Whether or not to display a `Total` section at the bottom of the tooltip.
   */
  showTotal: boolean;
  /**
   * Whether or not to display a label percentage for each data item value.
   */
  showPercentage: boolean;
  /**
   * The data key for the active data item, if any.
   */
  activeDataKey: TDataKeyType | undefined;
  /**
   * Whether to include the time as well as the date, if a date is provided.
   */
  showTime?: boolean;
  /**
   * Whether to include other rows besides the active row.
   */
  showOtherItems: boolean;
  /**
   * A render function to display a tooltip description.
   */
  descriptionRenderer?: (d: readonly TDatum[]) => React.ReactNode;
}

const Table = styled('table')({
  borderSpacing: 0,
  textAlign: 'left',
});

const FullWidthTable = styled(Table)({
  width: '100%',
});

const DateLabelCell = styled('td')(
  css({
    fontWeight: ThemeFontWeights.Medium,
    color: DateLabelText,
    pr: 1,
  }),
);

const DateValueCell = styled('td')(
  css({
    fontWeight: ThemeFontWeights.Medium,
    color: DateValueText,
  }),
);

const MoreResultsRow = ({ count }: { count: number }) => {
  const { t } = useTranslation();

  return (
    <tr>
      <Box as="td" colSpan={2} color={LabelText}>
        {t('{{count, INTEGER}} more result...', { count })}
      </Box>
    </tr>
  );
};

const DefaultTooltipContentInner = <TValueType extends number, TDataKeyType extends DataKeyType, TDatum>({
  dataKeyGetter,
  labelGetter,
  valueGetter,
  data,
  descriptionRenderer,
  activeDataKey,
  colorScale,
  title,
  showTotal: showTotalProp,
  showPercentage,
  date,
  showTime,
  showOtherItems,
}: DefaultTooltipContentProps<TValueType, TDataKeyType, TDatum>): JSX.Element => {
  const valueFormatter = useLocalizedStandardNumberFormatter();
  const labelPercentFormatter = useLocalizedLabelPercentFormatter();
  const percentFormatter = useLocalizedPercentFormatter();
  const { t } = useTranslation();

  const showTotal = showTotalProp && data.length > 1;
  const totalValue = useMemo(() => {
    if (showTotal || showPercentage) {
      return data.reduce((total, r) => valueGetter(r) + total, 0);
    }
    return 0;
  }, [data, showPercentage, showTotal, valueGetter]);

  const {
    filteredData,
    leadingOmitted = 0,
    trailingOmitted = 0,
  } = useMemo(() => {
    if (!showOtherItems) {
      return { filteredData: data.filter((d) => dataKeyGetter(d) === activeDataKey) };
    }

    if (data.length < TOOLTIP_MAX_ITEMS) {
      return { filteredData: data };
    }

    const activeIndex = data.findIndex((d) => dataKeyGetter(d) === activeDataKey);

    const { start: sliceStart, end: sliceEnd } = sliceCenter({
      inputLength: data.length,
      outputLength: TOOLTIP_MAX_ITEMS,
      outputMid: activeIndex,
    });

    // If we're only suppressing one value on either side, just extend the range to include it
    // instead
    const start = sliceStart === 1 ? 0 : sliceStart;
    const end = sliceEnd === data.length - 1 ? data.length : sliceEnd;

    return {
      filteredData: data.slice(start, end) as readonly Readonly<TDatum>[],
      leadingOmitted: start,
      trailingOmitted: data.length - end,
    };
  }, [activeDataKey, data, dataKeyGetter, showOtherItems]);

  const locale = useLocale();
  const { localFormattedDate, utcFormattedDate } = useMemo(() => {
    if (!date) {
      return {};
    }

    return {
      localFormattedDate: getLocalFormattedDate(date, locale, !!showTime),
      utcFormattedDate: getUTCFormattedDate(date, !!showTime),
    };
  }, [date, locale, showTime]);

  return (
    <Box
      as={Stack}
      p={1}
      textAlign="left"
      direction="column"
      itemSpacing={2}
      itemDividers
      width={`${TOOLTIP_CONTENT_WIDTH}px`}
    >
      {(title || descriptionRenderer || localFormattedDate || utcFormattedDate) && (
        <Stack direction="column" itemSpacing={1}>
          {title && (
            <Box fontWeight={ThemeFontWeights.Bold} fontSize="16px">
              {title}
            </Box>
          )}
          {descriptionRenderer && (
            <Box fontWeight={ThemeFontWeights.Bold} fontSize="12px">
              {descriptionRenderer(data)}
            </Box>
          )}
          {(localFormattedDate || utcFormattedDate) && (
            <Table>
              <tbody>
                <tr>
                  <DateLabelCell>{t('Local')}</DateLabelCell>
                  <DateValueCell>{localFormattedDate}</DateValueCell>
                </tr>
                <tr>
                  <DateLabelCell>{t('UTC')}</DateLabelCell>
                  <DateValueCell>{utcFormattedDate}</DateValueCell>
                </tr>
              </tbody>
            </Table>
          )}
        </Stack>
      )}
      <FullWidthTable>
        <tbody>
          {leadingOmitted > 0 && <MoreResultsRow count={leadingOmitted} />}
          {filteredData.map((row) => {
            const rowDataKey = dataKeyGetter(row);
            const color = colorScale.get(rowDataKey);
            const label = labelGetter(row);
            const value = valueGetter(row);

            return (
              <Box
                key={rowDataKey}
                as="tr"
                bg={rowDataKey === activeDataKey && filteredData.length > 1 ? ActiveFill : undefined}
              >
                <Box as="td" color={LabelText}>
                  <LegendItem color={color} label={label} allowWrap />
                </Box>
                <Box as="td" textAlign="right">
                  <NoWrapText>
                    <Box as="span" fontWeight={ThemeFontWeights.Bold} ml={1}>
                      {valueFormatter(value)}
                    </Box>
                    {showPercentage && (
                      <Box as="span" color={SubtleText}>
                        {' '}
                        {/* TODO: Revisit how we want to display percent ratios with negative values */}
                        {`(${labelPercentFormatter(value / (totalValue || 1))})`}
                      </Box>
                    )}
                  </NoWrapText>
                </Box>
              </Box>
            );
          })}
          {trailingOmitted > 0 && <MoreResultsRow count={trailingOmitted} />}
        </tbody>
      </FullWidthTable>
      {showTotal && (
        <Stack justifyContent="space-between">
          <Box color={LabelText}>{t('Total')}</Box>
          <StackChild>
            <Box as="span" fontWeight={ThemeFontWeights.Bold} ml={1}>
              {valueFormatter(totalValue)}
            </Box>
            {showPercentage && (
              <Box as="span" color={SubtleText}>
                {' '}
                {`(${percentFormatter(1)})`}
              </Box>
            )}
          </StackChild>
        </Stack>
      )}
    </Box>
  );
};

export const DefaultTooltipContent = React.memo(DefaultTooltipContentInner) as typeof DefaultTooltipContentInner;
